import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { ArrowsUpDownIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import DebouncedInput from "./DebouncedInput";
import { SearchIcon } from "../Icons/Icons";
import { useLoaderData, Link } from "react-router";
import { useAuthStore } from "../store/authStore";
import { useRecordStore } from "../store/recordStore";
import { parseSemanticVersion } from "../utilities/versionHelper";
import api from "../utilities/api";

const defaultPageSize = 10;
const columnHelper = createColumnHelper();

// The record fields TanStack Table's default global filter searches across
// (every `columnHelper.accessor` column in this file) — kept in sync with the
// `columns` definition so facet counts stay consistent with what the table
// actually shows for a given search term.
const SEARCHABLE_FIELDS = [
  "ofid",
  "fieldName",
  "dataType",
  "level",
  "tags",
  "linkReference",
  "introduced",
  "deprecated",
  "values",
  "example",
  "description",
];

// This gathers the record's id and sends it to the react-router
const ShowButton = ({ row }) => {
  const _id = `${row.original._id}`;
  return (
    <Link
      className="py-0 px-2 float-right mr-0 text-center text-cyan-900 font-normal text-base border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
      to={_id}
    >
      Show
    </Link>
  );
};

ShowButton.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({ _id: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
};

const FieldList = () => {
  // Subscribe to the role itself (not the isAdmin/isMember helpers) so the
  // list re-renders when it changes
  const role = useAuthStore((state) => state.user?.role);
  const isAdminUser = role === "admin";
  const isPrivileged = isAdminUser || role === "member";
  const canUseTagFilter = isPrivileged;
  const data = useLoaderData();

  const currentRecord = useRecordStore((state) => state.currentRecord);
  const setSelectedTags = useRecordStore((state) => state.setSelectedTags);
  const setTagOperation = useRecordStore((state) => state.setTagOperation);
  const setSelectedVersions = useRecordStore(
    (state) => state.setSelectedVersions,
  );

  // Combine related state updates into a single object
  const [tableState, setTableState] = useState({
    pageIndex: useRecordStore.getState().currentPage ?? 0,
    pageSize: useRecordStore.getState().currentPageSize || defaultPageSize,
    sorting: useRecordStore.getState().currentSorting || [],
    globalFilter: useRecordStore.getState().currentFilter || "",
  });

  // State for filter tags fetched from API
  const [filterTags, setFilterTags] = useState([]);
  // Admin-configured "current version" ceiling
  const [currentVersion, setCurrentVersion] = useState(null);

  // Fetch filter tags and current version setting on mount
  useEffect(() => {
    const fetchFilterTags = async () => {
      try {
        const response = await api.get(`/tags`);
        const tags = response.data.filter((tag) => tag.isFilter === true);
        setFilterTags(tags);
      } catch (error) {
        console.error("Error fetching filter tags:", error);
      }
    };

    const fetchCurrentVersion = async () => {
      try {
        const response = await api.get(`/settings/currentVersion`);
        setCurrentVersion(response.data.value);
      } catch {
        // No current version set yet — leave null (show all)
      }
    };

    fetchFilterTags();
    fetchCurrentVersion();
  }, []);

  // Tag filter state (stores tag names for backward compatibility with URL/store)
  const [selectedTagsLocal, setSelectedTagsLocal] = useState(
    useRecordStore.getState().selectedTags || [],
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Tag operation: "OR" = record has any selected tag, "AND" = all selected tags
  const [tagOperationLocal, setTagOperationLocal] = useState(
    useRecordStore.getState().tagOperation || "OR",
  );

  // Version filter state
  // Initialise with store selection; the useEffect below will recalculate
  // defaults once currentVersion has been fetched.
  const [selectedVersionsLocal, setSelectedVersionsLocal] = useState(() => {
    const stored = useRecordStore.getState().selectedVersion;
    if (stored && stored.length > 0) return stored;
    // Compute all available versions from loader data as default
    const versionSet = new Set();
    data.forEach((record) => {
      const introduced = record.introduced;
      if (introduced) {
        const versionStr = Array.isArray(introduced)
          ? introduced.join(".")
          : String(introduced);
        if (versionStr) versionSet.add(versionStr);
      }
    });
    return Array.from(versionSet);
  });
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] = useState(false);
  const versionDropdownRef = useRef(null);

  // Helper: compare two parsed version arrays, returns negative if a < b
  const compareVersions = useCallback((a, b) => {
    for (let i = 0; i < 3; i++) {
      const diff = a[i] - b[i];
      if (diff !== 0) return diff;
    }
    return 0;
  }, []);

  // All unique versions from data (sorted descending – newest first)
  const allVersions = useMemo(() => {
    const versionSet = new Set();
    data.forEach((record) => {
      const introduced = record.introduced;
      if (introduced) {
        const versionStr = Array.isArray(introduced)
          ? introduced.join(".")
          : String(introduced);
        if (versionStr) versionSet.add(versionStr);
      }
    });

    return Array.from(versionSet).sort((a, b) => {
      const parsedA = parseSemanticVersion(a) || [0, 0, 0];
      const parsedB = parseSemanticVersion(b) || [0, 0, 0];
      return compareVersions(parsedB, parsedA); // descending
    });
  }, [data, compareVersions]);

  // Versions available for the current user.
  // Non-privileged users only see versions ≤ currentVersion.
  // Privileged users see all versions.
  const availableVersions = useMemo(() => {
    if (!currentVersion) return allVersions; // no ceiling set → show all
    if (isPrivileged) return allVersions;

    const ceiling = parseSemanticVersion(currentVersion);
    if (!ceiling) return allVersions;

    return allVersions.filter((v) => {
      const parsed = parseSemanticVersion(v) || [0, 0, 0];
      return compareVersions(parsed, ceiling) <= 0;
    });
  }, [allVersions, currentVersion, isPrivileged, compareVersions]);

  // Determine if dropdown filters are active
  const isTagFilterActive = selectedTagsLocal.length > 0;
  // Version filter is "active" when the user has narrowed the selection,
  // i.e. not all versions are checked.
  const isVersionFilterActive =
    availableVersions.length > 0 &&
    selectedVersionsLocal.length < availableVersions.length;

  // Click-away listener for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        versionDropdownRef.current &&
        !versionDropdownRef.current.contains(event.target)
      ) {
        setIsVersionDropdownOpen(false);
      }
    };
    if (isDropdownOpen || isVersionDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, isVersionDropdownOpen]);

  // Whether a record passes the tag filter, honouring the OR/AND tag operation
  const recordPassesTagFilter = useCallback(
    (record) => {
      if (!isTagFilterActive) return true;
      const names = (record.tags || []).map((tag) =>
        typeof tag === "object" ? tag.name : tag,
      );
      return tagOperationLocal === "AND"
        ? selectedTagsLocal.every((name) => names.includes(name))
        : names.some((name) => selectedTagsLocal.includes(name));
    },
    [isTagFilterActive, selectedTagsLocal, tagOperationLocal],
  );

  // Filter data based on selected tags and versions (client-side).
  // This always runs; TanStack's own globalFilter (text search) is then
  // applied on top of this result, so search and dropdown filters combine.
  const filteredData = useMemo(() => {
    // Helper to get tag name from tag (handles both string and object)
    const getTagName = (tag) => (typeof tag === "object" ? tag.name : tag);

    // Step 1: Apply mandatory Public filter for non-privileged users
    // Guests and "user" role can ONLY see records with "Public" tag
    let baseData = data;
    if (!isPrivileged) {
      baseData = data.filter((record) =>
        record.tags?.some((tag) => getTagName(tag) === "Public"),
      );
    }

    // Step 2: Apply dropdown filters with AND logic between tags and versions
    return baseData.filter((record) => {
      // Check tag filter (OR: any selected tag, AND: all selected tags)
      const passesTagFilter = recordPassesTagFilter(record);

      // Version filter always applies: only selected versions pass.
      // No versions selected → no records shown.
      let passesVersionFilter = false;
      if (selectedVersionsLocal.length > 0) {
        const introduced = record.introduced;
        if (introduced) {
          const versionStr = Array.isArray(introduced)
            ? introduced.filter((v) => v !== null && v !== undefined).join(".")
            : String(introduced);
          passesVersionFilter = selectedVersionsLocal.includes(versionStr);
        }
      }

      // AND combination: record must pass both filters
      return passesTagFilter && passesVersionFilter;
    });
  }, [data, isPrivileged, recordPassesTagFilter, selectedVersionsLocal]);

  // Whether a record matches the current search text, using the same
  // case-insensitive substring match TanStack's default global filter uses
  // (see SEARCHABLE_FIELDS) — used below to compute facet counts.
  const recordMatchesSearch = useCallback(
    (record) => {
      const search = tableState.globalFilter.toLowerCase();
      if (!search) return true;
      return SEARCHABLE_FIELDS.some((field) => {
        const value = record[field];
        return value != null && String(value).toLowerCase().includes(search);
      });
    },
    [tableState.globalFilter],
  );

  // Facet counts for the Tags dropdown: for each tag, how many records would
  // match if that tag were also selected, given the current version
  // selection and search text (but not the current tag selection itself).
  const tagCounts = useMemo(() => {
    const counts = new Map();
    if (selectedVersionsLocal.length === 0) return counts;
    data.forEach((record) => {
      const versionStr = Array.isArray(record.introduced)
        ? record.introduced
            .filter((v) => v !== null && v !== undefined)
            .join(".")
        : String(record.introduced || "");
      if (!selectedVersionsLocal.includes(versionStr)) return;
      if (!recordMatchesSearch(record)) return;
      // In AND mode, adding a tag narrows the current selection, so only
      // records that already carry every selected tag can count.
      if (tagOperationLocal === "AND" && !recordPassesTagFilter(record)) return;
      record.tags?.forEach((tag) => {
        const name = typeof tag === "object" ? tag.name : tag;
        counts.set(name, (counts.get(name) || 0) + 1);
      });
    });
    return counts;
  }, [
    data,
    selectedVersionsLocal,
    recordMatchesSearch,
    tagOperationLocal,
    recordPassesTagFilter,
  ]);

  // Facet counts for the Versions dropdown: for each version, how many
  // records would match if that version were also selected, given the
  // current tag selection and search text (but not the current version
  // selection itself).
  const versionCounts = useMemo(() => {
    const counts = new Map();
    data.forEach((record) => {
      if (!recordPassesTagFilter(record)) return;
      if (!recordMatchesSearch(record)) return;
      const introduced = record.introduced;
      if (!introduced) return;
      const versionStr = Array.isArray(introduced)
        ? introduced.filter((v) => v !== null && v !== undefined).join(".")
        : String(introduced);
      if (!versionStr) return;
      counts.set(versionStr, (counts.get(versionStr) || 0) + 1);
    });
    return counts;
  }, [data, recordPassesTagFilter, recordMatchesSearch]);

  // Once currentVersion is fetched, apply it as the default selection
  // for privileged users (versions ≤ currentVersion pre-selected).
  // For non-privileged users availableVersions is already capped,
  // so select all of them.
  // If the admin changed currentVersion since the last visit, re-apply defaults.
  useEffect(() => {
    // Only run when currentVersion has loaded
    if (currentVersion === null) return;

    const { selectedVersion: stored, storedCurrentVersion, setStoredCurrentVersion } =
      useRecordStore.getState();

    // If currentVersion matches what we last stored, keep the user's selection
    if (storedCurrentVersion === currentVersion && stored && stored.length > 0) {
      return;
    }

    const ceiling = parseSemanticVersion(currentVersion);
    if (!ceiling) {
      // Invalid or missing — select everything
      setSelectedVersionsLocal(availableVersions);
      setSelectedVersions(availableVersions);
      setStoredCurrentVersion(currentVersion);
      return;
    }

    const defaults = availableVersions.filter((v) => {
      const parsed = parseSemanticVersion(v) || [0, 0, 0];
      return compareVersions(parsed, ceiling) <= 0;
    });

    setSelectedVersionsLocal(defaults);
    setSelectedVersions(defaults);
    setStoredCurrentVersion(currentVersion);
  }, [currentVersion, availableVersions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memorize colums definition
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "actions",
        header: isAdminUser ? (
          <Link
            className="py-0 px-2 float-right mr-0 text-center text-cyan-900 font-bold text-lg border-2 border-gray-500 bg-gray-300 hover:bg-gray-500 hover:text-white rounded-lg transition duration-300"
            to="create"
          >
            New
          </Link>
        ) : (
          <span></span>
        ),
        cell: ({ row }) => <ShowButton row={row} />,
        size: 100,
      }),
      columnHelper.accessor("ofid", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "OFID",
        size: 120,
      }),
      columnHelper.accessor("fieldName", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Field Name",
        size: 400,
      }),
      columnHelper.accessor("dataType", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Type",
        enableSorting: false,
        size: 150,
      }),
      columnHelper.accessor("level", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Level",
        size: 400,
      }),
      columnHelper.accessor("tags", {
        cell: (info) => {
          const value = info.getValue();
          return (
            <span>
              {Array.isArray(value) && value.length > 0
                ? value
                    .map((tag) => (typeof tag === "object" ? tag.name : tag))
                    .join(" | ")
                : "-"}
            </span>
          );
        },
        header: "Tags",
        enableSorting: false,
        size: 400,
      }),
      columnHelper.accessor("linkReference", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Reference",
        enableSorting: false,
        size: 400,
      }),
      columnHelper.accessor("introduced", {
        cell: (info) => {
          const value = info.getValue();
          return <span>{Array.isArray(value) ? value.join(".") : value}</span>;
        },
        header: "In",
        size: 150,
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) || [];
          const b = rowB.getValue(columnId) || [];
          // Compare each part of the version
          for (let i = 0; i < 3; i++) {
            const diff = (a[i] || 0) - (b[i] || 0);
            if (diff !== 0) return diff;
          }
          return 0;
        },
      }),
      columnHelper.accessor("deprecated", {
        cell: (info) => {
          const value = info.getValue();
          return <span>{Array.isArray(value) ? value.join(".") : value}</span>;
        },
        header: "Out",
        size: 150,
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) || [];
          const b = rowB.getValue(columnId) || [];
          // Compare each part of the version
          for (let i = 0; i < 3; i++) {
            const diff = (a[i] || 0) - (b[i] || 0);
            if (diff !== 0) return diff;
          }
          return 0;
        },
      }),
      columnHelper.accessor("values", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Values",
        enableSorting: false,
        size: 120,
      }),
      columnHelper.accessor("example", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Example",
        enableSorting: false,
        size: 120,
      }),
      columnHelper.accessor("description", {
        cell: (info) => <span>{info.getValue()}</span>,
        header: "Description",
        enableSorting: false,
        size: 400,
      }),
    ],
    [isAdminUser],
  );

  // Memorize table instance
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter: tableState.globalFilter,
      pagination: {
        pageIndex: tableState.pageIndex,
        pageSize: tableState.pageSize,
      },
      sorting: tableState.sorting,
    },
    onSortingChange: (updater) => {
      setTableState((prev) => ({
        ...prev,
        sorting:
          typeof updater === "function" ? updater(prev.sorting) : updater,
      }));
    },
    onPaginationChange: (updater) => {
      setTableState((prev) => {
        const newPagination =
          typeof updater === "function"
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return {
          ...prev,
          pageIndex: newPagination.pageIndex,
          pageSize: newPagination.pageSize,
        };
      });
    },
    autoResetPageIndex: false,
    initialState: {
      columnVisibility: { description: false },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Memoize filtered data (the rows array is itself memoized by TanStack Table)
  const prePaginationRows = table.getPrePaginationRowModel().rows;
  const filteredAndSortedData = useMemo(
    () => prePaginationRows.map((row) => row.original),
    [prePaginationRows],
  );

  // Store page specifications in global store
  useEffect(() => {
    const {
      setFields,
      setCurrentPage,
      setCurrentPageSize,
      setCurrentSorting,
      setCurrentFilter,
    } = useRecordStore.getState();

    if (Array.isArray(data) && data.length > 0) {
      setFields(filteredAndSortedData);
    }
    setCurrentPage(tableState.pageIndex);
    setCurrentPageSize(tableState.pageSize);
    setCurrentSorting(tableState.sorting);
    setCurrentFilter(tableState.globalFilter);
  }, [tableState, filteredAndSortedData, data]);

  const handleGlobalFilterChange = useCallback((value) => {
    const newFilter = String(value);
    setTableState((prev) => {
      if (newFilter === prev.globalFilter) return prev;
      return { ...prev, globalFilter: newFilter, pageIndex: 0 };
    });
  }, []);

  // Handle tag selection toggle
  const handleTagToggle = useCallback(
    (tag) => {
      setSelectedTagsLocal((prev) => {
        const newTags = prev.includes(tag)
          ? prev.filter((t) => t !== tag)
          : [...prev, tag];
        setSelectedTags(newTags);
        return newTags;
      });
      setTableState((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setSelectedTags],
  );

  // Handle version selection toggle
  const handleVersionToggle = useCallback(
    (version) => {
      setSelectedVersionsLocal((prev) => {
        const newVersions = prev.includes(version)
          ? prev.filter((v) => v !== version)
          : [...prev, version];
        setSelectedVersions(newVersions);
        return newVersions;
      });
      setTableState((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setSelectedVersions],
  );

  // Handle select all versions
  const handleSelectAllVersions = useCallback(() => {
    setSelectedVersionsLocal((prev) => {
      if (prev.length === availableVersions.length) return prev;
      setSelectedVersions(availableVersions);
      setTableState((tablePrev) => ({ ...tablePrev, pageIndex: 0 }));
      return availableVersions;
    });
  }, [availableVersions, setSelectedVersions]);

  // Handle clear all versions
  const handleClearAllVersions = useCallback(() => {
    setSelectedVersionsLocal((prev) => {
      if (prev.length === 0) return prev;
      setSelectedVersions([]);
      setTableState((tablePrev) => ({ ...tablePrev, pageIndex: 0 }));
      return [];
    });
  }, [setSelectedVersions]);

  // Handle resetting all filters: clear search, select all versions, deselect all tags
  const handleResetFilters = useCallback(() => {
    setSelectedVersionsLocal(availableVersions);
    setSelectedVersions(availableVersions);
    setSelectedTagsLocal([]);
    setSelectedTags([]);
    setTagOperationLocal("OR");
    setTagOperation("OR");
    setTableState((prev) => ({ ...prev, globalFilter: "", pageIndex: 0 }));
  }, [availableVersions, setSelectedVersions, setSelectedTags, setTagOperation]);

  // Handle the OR / AND toggle switch in the Tags dropdown
  const handleTagOperationToggle = useCallback(() => {
    const op = tagOperationLocal === "AND" ? "OR" : "AND";
    setTagOperationLocal(op);
    setTagOperation(op);
    setTableState((prev) => ({ ...prev, pageIndex: 0 }));
  }, [tagOperationLocal, setTagOperation]);

  // Recalculate page index when current record changes
  useEffect(() => {
    if (currentRecord != null && currentRecord >= 0) {
      const calculatedPageIndex = Math.floor(
        currentRecord / tableState.pageSize,
      );
      // Compare against prev instead of tableState.pageIndex so that manual
      // paging doesn't re-run this effect and jump back to the current record
      setTableState((prev) =>
        prev.pageIndex === calculatedPageIndex
          ? prev
          : { ...prev, pageIndex: calculatedPageIndex },
      );
    }
  }, [currentRecord, tableState.pageSize]);

  return (
    <>
      <div className="p-2 mx-auto text-black fill-gray-600">
        <div className="flex justify-between mb-2">
          <div className="pt-12 w-full flex flex-wrap items-center gap-4">
            {/* Text Search */}
            <div className="flex items-center gap-1">
              <SearchIcon />
              <DebouncedInput
                value={tableState.globalFilter ?? ""}
                onChange={handleGlobalFilterChange}
                className="bg-transparent outline-none border-b-2 w-48 focus:w-64 duration-300 border-gray-600"
                placeholder="Search ..."
              />
            </div>

            {/* Filter Dropdowns - Grouped so they wrap together */}
            {canUseTagFilter && (
              <div className="flex items-center gap-4">
                {/* Version Filter Dropdown */}
                <div className="relative" ref={versionDropdownRef}>
                  <button
                    onClick={() =>
                      setIsVersionDropdownOpen(!isVersionDropdownOpen)
                    }
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-all duration-200 bg-white text-gray-700 border-gray-300 hover:border-cyan-500 hover:text-cyan-700"
                  >
                    {isVersionFilterActive
                      ? `Versions (${selectedVersionsLocal.length})`
                      : "Versions"}
                    <ChevronDownIcon
                      className={`w-4 h-4 transition-transform duration-200 ${isVersionDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isVersionDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {/* Select All / Clear All buttons */}
                      <div className="flex gap-2 px-4 py-2 border-b border-gray-200">
                        <button
                          onClick={handleSelectAllVersions}
                          className="text-xs text-cyan-700 hover:text-cyan-900 font-medium transition-colors"
                        >
                          Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={handleClearAllVersions}
                          className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>
                      <div className="py-2 max-h-60 overflow-y-auto">
                        {availableVersions.map((version) => {
                          const isSelected =
                            selectedVersionsLocal.includes(version);
                          return (
                            <label
                              key={version}
                              className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleVersionToggle(version)}
                                className="w-4 h-4 text-cyan-700 border-gray-300 rounded focus:ring-cyan-500 focus:ring-2 accent-cyan-700"
                              />
                              <span className="text-sm text-gray-700">
                                {version}{" "}
                                <span className="text-gray-400 text-xs whitespace-nowrap">
                                  ({versionCounts.get(version) || 0})
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tag Filter Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-all duration-200 bg-white text-gray-700 border-gray-300 hover:border-cyan-500 hover:text-cyan-700"
                  >
                    Tags
                    {isTagFilterActive &&
                      ` (${selectedTagsLocal.length}${tagOperationLocal === "AND" ? ", AND" : ""})`}
                    <ChevronDownIcon
                      className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      {/* Tag operation switch: combine selected tags with OR (default) / AND */}
                      <div className="flex items-center justify-center gap-3 px-4 py-2 border-b border-gray-200 text-xs font-medium">
                        <span
                          className={
                            tagOperationLocal === "OR"
                              ? "text-cyan-700"
                              : "text-gray-400"
                          }
                        >
                          OR
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={tagOperationLocal === "AND"}
                          aria-label="Combine selected tags with AND"
                          onClick={handleTagOperationToggle}
                          className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-gray-300 bg-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-1"
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-full bg-cyan-700 shadow transition-transform duration-200 ${tagOperationLocal === "AND" ? "translate-x-[17px]" : "translate-x-px"}`}
                          />
                        </button>
                        <span
                          className={
                            tagOperationLocal === "AND"
                              ? "text-cyan-700"
                              : "text-gray-400"
                          }
                        >
                          AND
                        </span>
                      </div>
                      <div className="py-2">
                        {filterTags.map((tag) => {
                          const isSelected = selectedTagsLocal.includes(
                            tag.name,
                          );
                          return (
                            <label
                              key={tag._id}
                              className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleTagToggle(tag.name)}
                                className="w-4 h-4 text-cyan-700 border-gray-300 rounded focus:ring-cyan-500 focus:ring-2 accent-cyan-700"
                              />
                              <span className="text-sm text-gray-700">
                                {tag.name}{" "}
                                <span className="text-gray-400 text-xs whitespace-nowrap">
                                  ({tagCounts.get(tag.name) || 0})
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {isTagFilterActive && (
                        <div className="border-t border-gray-200 px-4 py-2">
                          <button
                            onClick={() => {
                              setSelectedTagsLocal([]);
                              setSelectedTags([]);
                              setTableState((prev) => ({
                                ...prev,
                                pageIndex: 0,
                              }));
                            }}
                            className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                          >
                            Clear selection
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Reset all filters: clear search, select all versions, deselect all tags */}
                <button
                  onClick={handleResetFilters}
                  className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                >
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-col">
          {/* <div className="-my-2 mx-4 sm:-mx-6 lg:-mx-8 overflow-x-auto"> */}
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full py-2 align-middle">
              <div>
                <table className="min-w-full text-left">
                  <thead className="bg-cyan-900 text-white">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            style={
                              header.column.columnDef.size
                                ? { width: header.column.columnDef.size }
                                : {}
                            }
                            className={`py-2 px-2 ${
                              header.id === "introduced"
                                ? "hidden sm:table-cell"
                                : ""
                            } ${
                              header.id === "example"
                                ? "hidden md:table-cell"
                                : ""
                            } ${
                              header.id === "level"
                                ? "hidden lg:table-cell"
                                : ""
                            } ${
                              header.id === "tags" ||
                              header.id === "linkReference"
                                ? "hidden xl:table-cell"
                                : ""
                            } ${
                              header.id.slice(-10) === "deprecated" ||
                              header.id === "values"
                                ? "hidden 2xl:table-cell"
                                : ""
                            }`}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {header.column.getCanSort() && (
                              <button
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                <ArrowsUpDownIcon
                                  className="h-6 w-6 text-white relative"
                                  style={{ top: "6px" }}
                                />
                              </button>
                            )}
                            {
                              {
                                asc: "🔼",
                                desc: "🔽",
                              }[header.column.getIsSorted()]
                            }
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row, i) => {
                        const isUnderReview = row.original.tags?.some(
                          (tag) =>
                            (typeof tag === "object" ? tag.name : tag) ===
                            "Under Review",
                        );
                        return (
                          <tr
                            key={row.id}
                            className={`${
                              isUnderReview
                                ? "bg-yellow-100"
                                : i % 2 === 0
                                  ? "bg-slate-100"
                                  : "bg-slate-300"
                            }`}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                className={`py-1 px-2 ${
                                  cell.id.slice(-10) === "introduced"
                                    ? "hidden sm:table-cell"
                                    : ""
                                } ${
                                  cell.id.slice(-7) === "example"
                                    ? "hidden md:table-cell"
                                    : ""
                                } ${
                                  cell.id.slice(-5) === "level"
                                    ? "hidden lg:table-cell"
                                    : ""
                                } ${
                                  cell.id.slice(-4) === "tags" ||
                                  cell.id.slice(-13) === "linkReference"
                                    ? "hidden xl:table-cell"
                                    : ""
                                } ${
                                  cell.id.slice(-10) === "deprecated" ||
                                  cell.id.slice(-6) === "values"
                                    ? "hidden 2xl:table-cell"
                                    : ""
                                }`}
                              >
                                <div className="line-clamp-1">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </div>
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    ) : (
                      <tr className="text-center h-32">
                        <td colSpan={12}>No Record Found!</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* pagination */}
        <div className="flex items-center justify-between mt-2 gap-1">
          {/* Under Review notice — shown only when highlighted rows are visible on current page */}
          {table
            .getRowModel()
            .rows.some((row) =>
              row.original.tags?.some(
                (tag) =>
                  (typeof tag === "object" ? tag.name : tag) === "Under Review",
              ),
            ) ? (
            <span className="bg-yellow-100 text-gray-700 text-sm px-2 py-1 rounded">
              Yellow marked lines indicate fields under review
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                table.firstPage();
              }}
              disabled={!table.getCanPreviousPage()}
              className="p-1 border rounded border-gray-300 px-1 py-0 disabled:opacity-30"
            >
              {"<<"}
            </button>
            <button
              onClick={() => {
                table.previousPage();
              }}
              disabled={!table.getCanPreviousPage()}
              className="p-1 border rounded border-gray-300 px-1 py-0 disabled:opacity-30"
            >
              {"<"}
            </button>
            <button
              onClick={() => {
                table.nextPage();
              }}
              disabled={!table.getCanNextPage()}
              className="p-1 border rounded border-gray-300 px-1 py-0 disabled:opacity-30"
            >
              {">"}
            </button>
            <button
              onClick={() => {
                table.lastPage();
              }}
              disabled={!table.getCanNextPage()}
              className="p-1 border rounded border-gray-300 px-1 py-0 disabled:opacity-30"
            >
              {">>"}
            </button>

            <span className="flex items-center gap-1 text-sm whitespace-nowrap">
              {/*  | Go to: */}
              <input
                type="number"
                value={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                className="border text-right rounded w-12 px-0.5 py-0.5 text-sm bg-transparent"
              />{" "}
              / {table.getPageCount()}
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              className="p-1 text-sm bg-transparent"
            >
              {[10, 25, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </>
  );
};

export default FieldList;
